import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Star } from "lucide-react";
import { Link } from "react-router-dom";
import { watchlistApi } from "@/api/ux";
import { Card } from "@/design-system/components/ui";
import { relativeTime } from "./relativeTime";

const ROUTE_MAP: Record<string, (id: string) => string> = {
  Broker: (id) => `/brokers/${id}`,
  Deal: (id) => `/pipeline/${id}`,
  Loan: (id) => `/portfolio-admin/loans/${id}`,
  Document: (id) => `/documents/${id}`,
  Report: (id) => `/reports/${id}`,
  Closing: (id) => `/closing/${id}`,
  SPV: (id) => `/spv/${id}`,
  Investor: (id) => `/investors/${id}`,
  Vendor: (id) => `/portfolio-admin/vendors/${id}`,
  Default: (id) => `/portfolio-admin/loans?tab=defaults&case=${id}`,
};

function resolveRoute(entityType: string, entityId: string): string {
  const resolver = ROUTE_MAP[entityType];
  return resolver ? resolver(entityId) : `/${entityType.toLowerCase()}/${entityId}`;
}

/**
 * UX Enhancements §5. Dashboard widget grouping the user's watched entities
 * by module. Keeps empty-state copy friendly — "Star anything..."
 */
export function WatchlistWidget() {
  const list = useQuery({
    queryKey: ["watchlist", "all"],
    queryFn: () => watchlistApi.list(),
  });

  const grouped = useMemo(() => {
    const rows = list.data ?? [];
    const groups = new Map<string, typeof rows>();
    for (const row of rows) {
      const existing = groups.get(row.entityType) ?? [];
      existing.push(row);
      groups.set(row.entityType, existing);
    }
    return [...groups.entries()].map(([type, items]) => ({ type, items }));
  }, [list.data]);

  return (
    <Card>
      <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow mb-space-2">
        Watching
      </div>
      <div className="font-display text-lead font-bold mb-space-3">Your starred items</div>

      {list.isLoading && (
        <div className="text-label uppercase tracking-label text-gray-mid">Loading…</div>
      )}

      {!list.isLoading && grouped.length === 0 && (
        <div className="flex flex-col items-center text-center py-space-4">
          <Star size={28} strokeWidth={1.5} className="text-gold-shadow mb-space-2" aria-hidden />
          <p className="text-small text-gray-mid m-0 max-w-reading">
            Star anything on a list page or detail header to track it here.
          </p>
        </div>
      )}

      {!list.isLoading && grouped.length > 0 && (
        <div className="flex flex-col gap-space-3">
          {grouped.map((group) => (
            <div key={group.type}>
              <div className="font-display text-label font-semibold uppercase tracking-label text-gray-mid mb-space-1">
                {group.type}
              </div>
              <ul className="m-0 p-0 list-none flex flex-col">
                {group.items.map((item) => (
                  <li
                    key={item.id}
                    className="border-t border-border-light first:border-t-0"
                  >
                    <Link
                      to={resolveRoute(item.entityType, item.entityId)}
                      className="flex items-center justify-between gap-space-2 py-space-2 text-small text-onyx hover:bg-gold-highlight/10 rounded-sm px-space-2 -mx-space-2"
                    >
                      <span className="truncate">
                        {item.entityTitle ?? `${item.entityType} ${item.entityId}`}
                      </span>
                      <span className="flex items-center gap-space-2 shrink-0">
                        <span className="text-label text-gray-mid">
                          {relativeTime(item.createdAt)}
                        </span>
                        <ChevronRight size={14} strokeWidth={1.5} className="text-gray-mid" aria-hidden />
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
